import { Injectable } from '@angular/core';

import { Observable, forkJoin as observableForkJoin, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClientService } from 'app/core/services/http-client.service';

import { Workplan, ResponseMetadata, AssemblyOrder } from 'app/model';
import { EndpointsConstants } from 'app/shared/constants/endpoints.constants';
import { AssemblyOrderService } from 'app/assembly-order/services/assembly-order.service';

@Injectable()
export class WorkplanService {

  constructor(
    private httpService: HttpClientService,
    private assemblyOrderService: AssemblyOrderService
  ) { }

  getAllPlans(params?: {}): Observable<{ workplans: Workplan[], metadata: ResponseMetadata }> {
    return this.httpService
      .get<{ workplans: Workplan[], metadata: ResponseMetadata }>(EndpointsConstants.workplan.all, params)
      .pipe(map(response => {
        return {
          workplans: response['data'].workplans.map(machine => new Workplan(machine)),
          metadata: response['data'].metadata
        };
      }));
  }

  getWorkplan(id: string): Observable<Workplan> {
    const endpointUrl = EndpointsConstants.workplan.single.replace('{id}', id);
    return this.httpService.get<Workplan>(endpointUrl).pipe(map(response => new Workplan(response['data'])));
  }

  saveWorkplan(workplan: Workplan): Observable<Workplan> {
    return this.httpService.post(EndpointsConstants.workplan.all, workplan)
      .pipe(map(response => new Workplan(response['data'])));
  }

  updateWorkplan(id: string, editedWorkplan: Workplan): Observable<Workplan> {
    const endpointUrl = EndpointsConstants.workplan.single.replace('{id}', id);
    return this.httpService.put(endpointUrl, editedWorkplan).pipe(map(response => new Workplan(response['data'])));
  }

  finishWorkplan(id: string): Observable<Workplan> {
    const endpointUrl = EndpointsConstants.workplan.finish.replace('{id}', id);
    return this.httpService.put(endpointUrl).pipe(map(response => new Workplan(response['data'])));
  }

  saveAdminComment(entity) {
    return this.httpService.post(EndpointsConstants.workplan.adminComment, entity);
  }

  saveRealDate(entity) {
    return this.httpService.post(EndpointsConstants.workplan.setDates, entity);
  }

  deleteWorkplan(id: string): Observable<{ message: string }> {
    const endpointUrl = EndpointsConstants.workplan.single.replace('{id}', id);
    return this.httpService.delete<{ message: string }>(endpointUrl);
  }

  migrateAssemblyOrdersToWorkplan( idAssemblyOrder: number, idWorkplan: number ): Observable<Workplan> {
    const endpointUrl = `${EndpointsConstants.assemblyOrder.all}/${idAssemblyOrder}${EndpointsConstants.workplan.base}/${idWorkplan}`;
    return this.httpService.put(endpointUrl);
  }

  swapAssemblyOrders(id: string, idAssemblyOrderOrigin: string, idAssemblyOrderDestination: string): Observable<{ message: string }> {
    const endpointUrl = EndpointsConstants.workplan.swap
      .replace('{idWorkplan}', id)
      .replace('{idAssemblyOrderOrigin}', idAssemblyOrderOrigin)
      .replace('{idAssemblyOrderDestination}', idAssemblyOrderDestination);

    return this.httpService.put(endpointUrl);
  }

  getGantt(id, params) {
    const endpointUrl = EndpointsConstants.workplan.gantt.replace('{id}', id);
     return this.httpService.get(endpointUrl, params);
  }

  getWorkplanFormData(workplanId?: string) {
    return observableForkJoin(
      this.assemblyOrderService.getOrphanAssemblyOrders(),
      workplanId ? this.getWorkplan(workplanId) : observableOf(null)
    ).pipe(map((data: [AssemblyOrder[], Workplan]) => {
      const [ assemblyOrders, workplan ] = data;
      return { assemblyOrders, workplan };
    }));
  }
}
